"use client";

import { Mail } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { MagicLinkForm } from "@/components/forms/magic-link-form";
import { useJobContext } from "@/components/jobs/job-context";
import { Button } from "@/components/ui/button";
import {
	ALERT_CAPTURE_PARAM,
	buildJobAlertTarget,
	JOBDETAIL_ALERT_CAPTURE_FLAG,
	JOBDETAIL_ALERT_SOURCE,
	type JobAlertFacets,
} from "@/lib/experiments/jobdetail-alert-capture";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";

type Props = JobAlertFacets & {
	jobId: string;
	locale: Locale;
	/** `/{locale}/jobs/{slug}` — the magic link returns here. */
	jobPathname: string;
	t?: Dictionary;
	/** Runtime Turnstile site key from the page (see `getTurnstileSiteKey`). */
	turnstileSiteKey?: string;
};

type SubscribeState =
	| "idle"
	| "saving"
	| "saved"
	| "limit"
	| "error"
	| "needs_login";

/**
 * "Kirim loker seperti ini ke email kamu" — job-detail alert capture, gated by
 * the `jobdetail-alert-capture` PostHog flag. See
 * `src/lib/experiments/jobdetail-alert-capture.ts` for why it is built from
 * MagicLinkForm + /api/saved-searches rather than a guest-only list.
 *
 * The capture UI shows only once the flag resolves true AND the session state
 * is known. While either is still unknown it renders an invisible,
 * `aria-hidden` placeholder with the guest card's geometry, so the sidebar
 * cards below do not jump down when the card appears. That jump was the top
 * job-detail layout shift in PostHog web vitals: 37 human page views from
 * 2026-10-17 to 2026-10-31 attributed to the sidebar card below this slot,
 * median ~3.5 s after load. The placeholder is identical for every visitor, so
 * the cached guest HTML stays deterministic. It collapses once the flag says
 * false, or after `RESERVE_TIMEOUT_MS` when PostHog never answers (ad-blocked).
 */
const RESERVE_TIMEOUT_MS = 4000;
export function JobDetailAlertCapture({
	jobId,
	locale,
	jobPathname,
	t,
	turnstileSiteKey,
	categorySlug,
	categoryName,
	skills,
	seniority,
}: Props) {
	const posthog = usePostHog();
	const { loading, isAuthenticated } = useJobContext();
	const [enabled, setEnabled] = useState<boolean | null>(null);
	const [returning, setReturning] = useState(false);
	const [state, setState] = useState<SubscribeState>("idle");
	const rootRef = useRef<HTMLElement>(null);
	const shownRef = useRef(false);
	const submittedRef = useRef(false);
	const replayedRef = useRef(false);

	const target = useMemo(
		() =>
			buildJobAlertTarget({ categorySlug, categoryName, skills, seniority }),
		[categorySlug, categoryName, skills, seniority],
	);
	const isId = locale !== "en";

	const props = useMemo(
		() => ({
			source: JOBDETAIL_ALERT_SOURCE,
			job_id: jobId,
			locale,
			filter_kind: target?.kind ?? null,
			category_slug: categorySlug,
			top_skill: skills[0] ?? null,
			seniority,
		}),
		[jobId, locale, target, categorySlug, skills, seniority],
	);

	const [reserveExpired, setReserveExpired] = useState(false);

	// Flag: the sole switch. `getFeatureFlag` returns `undefined` until PostHog
	// has loaded flags, so `undefined` stays `null` (unknown: hold the reserved
	// slot); any loaded answer other than `true` is off.
	useEffect(() => {
		if (!posthog) return;
		const check = () => {
			const value = posthog.getFeatureFlag(JOBDETAIL_ALERT_CAPTURE_FLAG);
			setEnabled(value === undefined ? null : value === true);
		};
		check();
		return posthog.onFeatureFlags?.(check);
	}, [posthog]);

	// Never hold an empty slot forever: an ad-blocked PostHog never answers.
	useEffect(() => {
		const timer = window.setTimeout(
			() => setReserveExpired(true),
			RESERVE_TIMEOUT_MS,
		);
		return () => window.clearTimeout(timer);
	}, []);

	// Magic-link return trip: `?alert_capture=job_detail` is read from the
	// browser URL only, never server-side, so the ISR HTML stays deterministic.
	// Honoured even if the flag was turned off since — the visitor already
	// asked for the alert.
	useEffect(() => {
		try {
			const params = new URLSearchParams(window.location.search);
			if (params.get(ALERT_CAPTURE_PARAM) === JOBDETAIL_ALERT_SOURCE) {
				setReturning(true);
			}
		} catch {
			// No usable location — treat as a normal visit.
		}
	}, []);

	const subscribe = useCallback(async () => {
		if (!target) return;
		setState("saving");
		try {
			const res = await fetch("/api/saved-searches", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({
					filters: target.filters,
					locale,
					source: JOBDETAIL_ALERT_SOURCE,
				}),
			});
			if (res.ok) setState("saved");
			else if (res.status === 409) setState("limit");
			else if (res.status === 401) setState("needs_login");
			else setState("error");
		} catch {
			setState("error");
		}
	}, [target, locale]);

	useEffect(() => {
		if (!returning || loading || replayedRef.current || !target) return;
		replayedRef.current = true;
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete(ALERT_CAPTURE_PARAM);
			window.history.replaceState(window.history.state, "", url.toString());
		} catch {
			// Cosmetic only.
		}
		if (isAuthenticated) void subscribe();
	}, [returning, loading, isAuthenticated, target, subscribe]);

	const visible =
		Boolean(target) && !loading && (enabled === true || returning);

	// One impression per mount, when the block actually scrolls into view.
	useEffect(() => {
		const node = rootRef.current;
		if (!visible || !node || shownRef.current) return;
		if (typeof IntersectionObserver === "undefined") return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting) && !shownRef.current) {
					shownRef.current = true;
					posthog?.capture("alert_capture_shown", {
						...props,
						signed_in: isAuthenticated,
					});
					observer.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [visible, posthog, props, isAuthenticated]);

	// Pending = the card may still appear: flag unknown, or flag on but the
	// session state still loading. Off, expired, or no target = nothing.
	const pending =
		Boolean(target) &&
		!visible &&
		!reserveExpired &&
		(enabled === null || enabled === true || returning);

	if (pending && target) {
		return <AlertCaptureReserve isId={isId} label={target.label} />;
	}

	if (!visible || !target) return null;

	function trackSubmitted(mode: "magic_link" | "one_click") {
		if (submittedRef.current) return;
		submittedRef.current = true;
		posthog?.capture("alert_capture_submitted", { ...props, mode });
	}

	const title = alertTitle(isId);
	const body = alertBody(isId, target.label);

	let content: ReactNode;
	if (state === "saved") {
		content = (
			<p
				role="status"
				className="text-sm text-foreground"
				data-testid="jobdetail-alert-saved"
			>
				{isId
					? `Beres — lowongan ${target.label} baru akan masuk ke email kamu.`
					: `Done — new ${target.label} jobs will land in your inbox.`}
			</p>
		);
	} else if (state === "limit") {
		content = (
			<p role="status" className="text-sm text-muted-foreground">
				{isId
					? "Kamu sudah mencapai batas alert. Hapus salah satu alert lama dulu untuk menambah yang baru."
					: "You've reached the alert limit. Remove an older alert to add this one."}
			</p>
		);
	} else if (isAuthenticated && state !== "needs_login") {
		content = (
			<div className="space-y-2">
				<Button
					type="button"
					size="sm"
					shape="pill"
					disabled={state === "saving"}
					onClick={() => {
						trackSubmitted("one_click");
						void subscribe();
					}}
					data-testid="jobdetail-alert-subscribe"
				>
					{state === "saving"
						? isId
							? "Menyimpan…"
							: "Saving…"
						: isId
							? "Kirim ke email saya"
							: "Send them to my email"}
				</Button>
				{state === "error" && (
					<p className="text-xs text-destructive" role="alert">
						{isId
							? "Gagal menyimpan alert. Coba lagi."
							: "Couldn't save the alert. Please try again."}
					</p>
				)}
			</div>
		);
	} else {
		const callbackURL = `${jobPathname}?${ALERT_CAPTURE_PARAM}=${JOBDETAIL_ALERT_SOURCE}`;
		content = (
			// Capture-phase listener: MagicLinkForm owns its submit (Turnstile,
			// validation, resend); this only records that the visitor tried.
			<div onSubmitCapture={() => trackSubmitted("magic_link")}>
				<MagicLinkForm
					locale={locale}
					t={t}
					callbackURL={callbackURL}
					turnstileSiteKey={turnstileSiteKey}
				/>
			</div>
		);
	}

	return (
		<section
			ref={rootRef}
			aria-labelledby="jobdetail-alert-title"
			data-testid="jobdetail-alert-capture"
			className="rounded-xl border border-border/60 bg-card p-4"
		>
			<h2
				id="jobdetail-alert-title"
				className="flex items-center gap-2 text-sm font-semibold text-foreground"
			>
				<Mail aria-hidden="true" className="size-4 text-primary" />
				{title}
			</h2>
			{state !== "saved" && (
				<p className="mt-1 mb-3 text-sm text-muted-foreground">{body}</p>
			)}
			{content}
		</section>
	);
}

function alertTitle(isId: boolean): string {
	return isId
		? "Kirim loker seperti ini ke email kamu"
		: "Email me jobs like this";
}

function alertBody(isId: boolean, label: string): string {
	return isId
		? `Lowongan ${label} baru langsung ke inbox. Berhenti kapan saja lewat link di setiap email.`
		: `New ${label} jobs straight to your inbox. Stop anytime from the link in every email.`;
}

/**
 * Invisible stand-in with the guest card's geometry: the same section padding
 * and border, a one-line title row, the real body text (so wrapping matches),
 * and MagicLinkForm's stack (label + input, the 65px Turnstile reservation, the
 * "verifying" hint, the large submit button). `invisible` keeps the box in
 * layout without painting it; `aria-hidden` + `inert` keep it out of the
 * accessibility tree and the tab order.
 */
function AlertCaptureReserve({
	isId,
	label,
}: {
	isId: boolean;
	label: string;
}) {
	return (
		<div
			aria-hidden="true"
			inert
			data-testid="jobdetail-alert-capture-reserve"
			className="invisible rounded-xl border border-border/60 p-4"
		>
			<p className="flex items-center gap-2 text-sm font-semibold">
				{/* The title is one line; a blank line holds its height without
				    putting a second copy of the heading text in the DOM. */}
				<Mail className="size-4" />
				&nbsp;
			</p>
			<p className="mt-1 mb-3 text-sm">{alertBody(isId, label)}</p>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<span className="text-sm font-medium">Email</span>
					<span className="block w-full rounded-xl border border-border/40 px-4 py-3 text-base">
						&nbsp;
					</span>
				</div>
				<span className="block min-h-[65px]" />
				<span className="text-xs">&nbsp;</span>
				<span className="block w-full rounded-full px-6 py-3 text-base">
					&nbsp;
				</span>
			</div>
		</div>
	);
}
